import { addDays, format, getDate, startOfWeek } from "date-fns";
import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchEmployeesForPlanning,
  fetchTimesheet,
  saveTimesheetBatch,
  deleteTimesheetEntriesForPlannable,
} from "./api";
import { queryClient } from "utils/react-query";
import { toast } from "sonner";

const useTimesheetPlanning = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null);
  const [localPlannables, setLocalPlannables] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const {
    data: users = [],
    isSuccess: isUsersSuccess,
    isLoading: isLoadingUsers,
    isError: isUsersError,
    error: usersError,
  } = useQuery({
    queryKey: ["users", "planning"],
    queryFn: fetchEmployeesForPlanning,
  });

  console.log(users);

  useEffect(() => {
    if (isUsersSuccess && users.length > 0 && !selectedUser) {
      setSelectedUser(users[0]);
    }
  }, [isUsersSuccess, users, selectedUser]);

  const getWeekData = useCallback(() => {
    const weekData = [];
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });

    for (let i = 0; i < 7; i++) {
      const date = addDays(weekStart, i);
      weekData.push({
        day: format(date, "EEE"),
        date: getDate(date),
        month: format(date, "MMM"),
        fullDate: format(date, "yyyy-MM-dd"),
      });
    }
    return weekData;
  }, [currentDate]);

  const weekData = getWeekData();
  const startDate = weekData[0]?.fullDate;
  const endDate = weekData[6]?.fullDate;

  const {
    data: timesheetData = [],
    isLoading: isLoadingTimesheet,
    isError: isTimesheetError,
    error: timesheetError,
    isSuccess: isTimesheetSuccess,
    isFetching: isFetchingTimesheet,
    refetch: refetchTimesheet,
  } = useQuery({
    queryKey: ["timesheet", "planning", selectedUser?.id, startDate, endDate],
    queryFn: () => fetchTimesheet(selectedUser.id, startDate, endDate),
    enabled: !!selectedUser && !!startDate && !!endDate,
  });

  const processedPlannables = useMemo(() => {
    const plannables = timesheetData.reduce((acc, item) => {
      const key = `${item.project.id}:${item.job.id}:${
        item.milestone?.id || ""
      }`;
      if (!acc[key]) {
        acc[key] = {
          id: item.id,
          project: item?.project,
          job: item?.job,
          milestone: item?.milestone,
          planned_hours: {},
          existing_dates: {}, // Track which dates have existing data
        };
      }
      acc[key].planned_hours[item.date] = item.planned_hours;
      acc[key].existing_dates[item.date] = true; // Mark this date as existing
      return acc;
    }, {});

    return Object.values(plannables);
  }, [timesheetData]);

  useEffect(() => {
    if (isTimesheetSuccess && processedPlannables.length > 0) {
      setLocalPlannables(processedPlannables);
      setHasUnsavedChanges(false); // Reset changes when data is loaded from server
    } else if (isTimesheetSuccess && processedPlannables.length === 0) {
      setLocalPlannables([]);
      setHasUnsavedChanges(false);
    }
  }, [isTimesheetSuccess, processedPlannables]);

  const handleUserChange = (user) => {
    setSelectedUser(user);
  };

  const handleWeekChange = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === "next" ? 7 : -7));
    setCurrentDate(newDate);
  };

  const handleDateChange = (date) => {
    setCurrentDate(date);
  };

  const handleAddPlannable = (plannable) => {
    const existingPlannable = localPlannables.find((p) => {
      return (
        p.project.id === plannable.project.value &&
        p.job.id === plannable.job.value &&
        p.milestone?.id === plannable.milestone?.value
      );
    });

    if (existingPlannable) {
      toast.error(
        "The Selected Combination of project job and milestone already exists in the table",
      );
      return;
    }

    const updatedPlannable = {
      id: `${plannable.project.value}:${plannable.job.value}:${
        plannable.milestone?.value || ""
      }`,
      project: {
        id: plannable.project.value,
        name: plannable.project.label,
      },
      job: {
        id: plannable.job.value,
        name: plannable.job.label,
        start_date: plannable.job.start_date,
        end_date: plannable.job.end_date,
      },
      ...(plannable?.milestone?.value
        ? {
            milestone: {
              id: plannable.milestone.value,
              name: plannable.milestone.label,
              start_date: plannable.milestone.start_date,
              end_date: plannable.milestone.end_date,
            },
          }
        : {}),
      planned_hours: {},
      existing_dates: {}, // Track which dates have existing data
    };

    const newPlannables = [...localPlannables, updatedPlannable];
    setLocalPlannables(newPlannables);
    setHasUnsavedChanges(true);
  };

  const getPlannableId = useCallback((plannable) => {
    return `${plannable.project.id}:${plannable.job.id}:${
      plannable.milestone?.id || ""
    }`;
  }, []);

  const getPlannableTotal = useCallback((plannable) => {
    return Object.values(plannable.planned_hours || {}).reduce(
      (a, b) => a + b,
      0,
    );
  }, []);

  const getDayTotal = useCallback(
    (day) => {
      return localPlannables.reduce((total, plannable) => {
        return total + (plannable.planned_hours?.[day.fullDate] || 0);
      }, 0);
    },
    [localPlannables],
  );

  const getWeekTotal = useCallback(() => {
    return localPlannables.reduce((total, plannable) => {
      return total + getPlannableTotal(plannable);
    }, 0);
  }, [localPlannables, getPlannableTotal]);

  const handleHourChange = useCallback(
    (plannable, day, hours) => {
      // Check if date has existing data
      if (plannable.existing_dates?.[day.fullDate]) {
        return; // Don't allow editing existing entries for this specific date
      }

      // Check if date is within valid range (milestone or job dates)
      const isDateInValidRange = (plannable, date) => {
        // If milestone exists, check milestone date range
        if (plannable.milestone?.start_date && plannable.milestone?.end_date) {
          const cellDate = new Date(date);
          const milestoneStart = new Date(plannable.milestone.start_date);
          const milestoneEnd = new Date(plannable.milestone.end_date);
          return cellDate >= milestoneStart && cellDate <= milestoneEnd;
        }

        // If no milestone but job exists, check job date range
        if (plannable.job?.start_date && plannable.job?.end_date) {
          const cellDate = new Date(date);
          const jobStart = new Date(plannable.job.start_date);
          const jobEnd = new Date(plannable.job.end_date);
          return cellDate >= jobStart && cellDate <= jobEnd;
        }

        // If no date ranges available, allow editing
        return true;
      };

      if (!isDateInValidRange(plannable, day.fullDate)) {
        return; // Don't allow editing outside valid date range
      }

      const newPlannables = localPlannables.map((p) => {
        if (getPlannableId(p) === getPlannableId(plannable)) {
          const newPlannedHours = { ...p.planned_hours };
          if (hours) {
            newPlannedHours[day.fullDate] = parseInt(hours);
          } else {
            delete newPlannedHours[day.fullDate];
          }
          return { ...p, planned_hours: newPlannedHours };
        }
        return p;
      });

      setLocalPlannables(newPlannables);
      setHasUnsavedChanges(true);
    },
    [localPlannables, getPlannableId],
  );

  const saveTimesheetMutation = useMutation({
    mutationFn: (plannables) => saveTimesheetBatch(selectedUser.id, plannables),
    onSuccess: () => {
      queryClient.invalidateQueries();
      setHasUnsavedChanges(false); // Reset after successful save
    },
    onError: (error) => {
      console.error("Error saving timesheet:", error);
    },
  });

  const deleteTimesheetMutation = useMutation({
    mutationFn: ({ plannable, startDate, endDate }) =>
      deleteTimesheetEntriesForPlannable(
        selectedUser.id,
        plannable.project.id,
        plannable.job.id,
        plannable.milestone?.id || null,
        startDate,
        endDate,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error("Error deleting timesheet entries:", error);
    },
  });

  const saveTimesheet = () => {
    if (selectedUser && localPlannables.length > 0) {
      // Filter plannables to only include entries with new (non-existing) dates
      const plannablesToSave = localPlannables
        .map((plannable) => {
          const newPlannedHours = {};

          // Only include dates that are not in existing_dates
          Object.keys(plannable.planned_hours || {}).forEach((date) => {
            if (
              !plannable.existing_dates?.[date] &&
              plannable.planned_hours[date] > 0
            ) {
              newPlannedHours[date] = plannable.planned_hours[date];
            }
          });

          // Only return plannable if it has new hours to save
          if (Object.keys(newPlannedHours).length > 0) {
            return {
              ...plannable,
              planned_hours: newPlannedHours,
            };
          }
          return null;
        })
        .filter(Boolean);

      if (plannablesToSave.length > 0) {
        saveTimesheetMutation.mutate(plannablesToSave);
      }
    }
  };

  const handleDeletePlannable = useCallback(
    (plannable) => {
      if (selectedUser) {
        const weekData = getWeekData();
        const startDate = weekData[0]?.fullDate;
        const endDate = weekData[6]?.fullDate;

        const newPlannables = localPlannables.filter(
          (p) => getPlannableId(p) !== getPlannableId(plannable),
        );
        setLocalPlannables(newPlannables);
        deleteTimesheetMutation.mutate({
          plannable,
          startDate,
          endDate,
        });
      }
    },
    [
      selectedUser,
      localPlannables,
      getPlannableId,
      deleteTimesheetMutation,
      getWeekData,
    ],
  );

  return {
    timesheet: { plannables: localPlannables, selectedUser },
    setTimesheet: setLocalPlannables,
    getWeekData,
    handleUserChange,
    handleWeekChange,
    handleDateChange,
    handleHourChange,
    currentDate,
    selectedUser,
    users,

    loading: isLoadingTimesheet,
    updateLoading: saveTimesheetMutation.isPending,
    isLoadingUsers,
    isLoadingTimesheet,
    isFetchingTimesheet,

    isUsersSuccess,
    isTimesheetSuccess,

    isUsersError,
    isTimesheetError,
    usersError,
    timesheetError,

    saveTimesheet,
    handleAddPlannable,
    handleDeletePlannable,
    refetchTimesheet,

    getPlannableId,
    getPlannableTotal,
    getDayTotal,
    getWeekTotal,
    saveTimesheetMutation,
    deleteTimesheetMutation,

    hasUnsavedChanges,
  };
};

export default useTimesheetPlanning;
